import { Radio, RadioGroup } from "@headlessui/react";
import { Typography, Icon, Badge } from "@/components/atoms";

interface SubscriptionGroupProps extends React.ComponentProps<typeof RadioGroup> {
  children: React.ReactNode;
}

export const SubscriptionGroup = ({ value, onChange, children }: SubscriptionGroupProps) => {
  return (
    <RadioGroup value={value} onChange={onChange} className="mt-6 flex flex-col gap-2.5">
      {children}
    </RadioGroup>
  );
};

interface SubscriptionGroupOptionProps extends React.ComponentProps<typeof Radio> {
  title: string;
  description: string;
  pricing: string;
  iconSvg?: string;
  logo?: JSX.Element;
  trialDays?: number;
  disabled?: boolean;
  disabledReason?: string;
}

const SubscriptionGroupOption = ({
  title,
  description,
  pricing,
  iconSvg,
  logo,
  value,
  trialDays,
  disabled,
  disabledReason,
}: SubscriptionGroupOptionProps) => {
  return (
    <Radio
      value={value}
      disabled={disabled}
      className="flex h-fit w-full flex-row gap-3 rounded-lg border border-neutral-mid-gray bg-white p-5 data-[disabled]:cursor-not-allowed data-[checked]:border-primary data-[disabled]:data-[checked]:border-neutral-mid-gray data-[disabled]:opacity-50 data-[checked]:ring-2 data-[disabled]:data-[checked]:ring-0 data-[checked]:ring-primary-light [&:not([data-disabled])]:cursor-pointer"
    >
      {({ checked, disabled: isDisabled }) => (
        <div className="flex w-full flex-row items-start justify-between gap-4">
          <div className="flex flex-1 flex-row items-start gap-4">
            <input
              type="radio"
              className="pointer-events-none mt-0.5 h-5 w-5 cursor-pointer border-neutral-mid-gray text-primary focus:ring-0 disabled:border-neutral-dark-gray"
              checked={checked}
              disabled={disabled}
              readOnly
              tabIndex={-1}
              aria-hidden="true"
            />
            {iconSvg ? (
              <div className="h-6 w-6 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: iconSvg }} />
            ) : (
              logo
            )}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <Typography variant="title" className="text-neutral-dark-gray">
                  {title}
                </Typography>
                {trialDays && trialDays > 0 && !isDisabled && (
                  <div className="inline-flex items-center gap-1 rounded-full bg-success-light px-2 py-1 text-success">
                    <Icon className="flex-shrink-0" name="Timer1" variant="Bold" size="xs" />
                    <Typography className="!text-sm !leading-none" variant="action">
                      {trialDays} days free
                    </Typography>
                  </div>
                )}
              </div>
              {isDisabled && disabledReason && <Badge type="disabled" message={disabledReason} size="sm" className="self-start" />}
              {description && (
                <Typography
                  variant="paragraph"
                  className="text-neutral-dark-gray [&_a]:text-info [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0">
            <Typography variant="title">{pricing}</Typography>
          </div>
        </div>
      )}
    </Radio>
  );
};

SubscriptionGroup.Option = SubscriptionGroupOption;
